import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Package as PackageIcon } from "lucide-react";
import { EQUIPMENT_PACKAGES, findEquipmentById, formatIDR } from "@/data/equipmentPackages";

export default function BookingDialog({ pkg, children }) {
  const { lang, t } = useLang();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    pax: 1,
    departure_pref: "",
    notes: "",
    equipment_id: "",
  });

  const handle = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const selectedEq = findEquipmentById(form.equipment_id);
  const pax = parseInt(form.pax, 10) || 1;
  const eqTotal = selectedEq ? (selectedEq.price_idr * pax) : 0;
  const packageName = lang === "id" ? pkg.name_id : pkg.name_en;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        package_id: pkg.id,
        package_name: packageName,
        name: form.name,
        email: form.email,
        phone: form.phone,
        pax,
        departure_pref: form.departure_pref || null,
        notes: form.notes || null,
      };
      if (selectedEq) {
        payload.equipment_id = selectedEq.id;
        payload.equipment_name = lang === "id" ? selectedEq.name_id : selectedEq.name_en;
        payload.equipment_price_idr = selectedEq.price_idr;
      }
      await api.post("/bookings", payload);
      toast.success(t("booking.success"));
      setForm({ name: "", email: "", phone: "", pax: 1, departure_pref: "", notes: "", equipment_id: "" });
      setOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(t("booking.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="max-w-lg bg-background border-border max-h-[92vh] overflow-y-auto"
        data-testid="booking-dialog"
      >
        <DialogHeader>
          <p className="overline">{t("nav.book")}</p>
          <DialogTitle className="font-serif text-2xl text-primary">{packageName}</DialogTitle>
          <DialogDescription className="text-muted-foreground">{t("booking.sub")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="bk-name" className="text-xs uppercase tracking-wider text-muted-foreground">{t("booking.name")}</Label>
            <Input id="bk-name" data-testid="booking-name" required value={form.name} onChange={handle("name")} className="mt-1.5 bg-white" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bk-email" className="text-xs uppercase tracking-wider text-muted-foreground">{t("booking.email")}</Label>
              <Input id="bk-email" type="email" data-testid="booking-email" required value={form.email} onChange={handle("email")} className="mt-1.5 bg-white" />
            </div>
            <div>
              <Label htmlFor="bk-phone" className="text-xs uppercase tracking-wider text-muted-foreground">{t("booking.phone")}</Label>
              <Input id="bk-phone" data-testid="booking-phone" required value={form.phone} onChange={handle("phone")} className="mt-1.5 bg-white" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bk-pax" className="text-xs uppercase tracking-wider text-muted-foreground">{t("booking.pax")}</Label>
              <Input id="bk-pax" type="number" min="1" data-testid="booking-pax" required value={form.pax} onChange={handle("pax")} className="mt-1.5 bg-white" />
            </div>
            <div>
              <Label htmlFor="bk-pref" className="text-xs uppercase tracking-wider text-muted-foreground">{t("booking.pref")}</Label>
              <Input id="bk-pref" data-testid="booking-pref" value={form.departure_pref} onChange={handle("departure_pref")} className="mt-1.5 bg-white" placeholder={pkg.departure || ""} />
            </div>
          </div>

          {/* Equipment selector */}
          <div>
            <Label htmlFor="bk-equipment" className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <PackageIcon className="h-3.5 w-3.5 text-accent" />
              {lang === "id" ? "Paket Perlengkapan (Opsional)" : "Equipment Package (Optional)"}
            </Label>
            <select
              id="bk-equipment"
              data-testid="booking-equipment"
              value={form.equipment_id}
              onChange={handle("equipment_id")}
              className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="">
                {lang === "id" ? "— Tidak ambil perlengkapan —" : "— No equipment —"}
              </option>
              {EQUIPMENT_PACKAGES.map((eq) => (
                <option key={eq.id} value={eq.id}>
                  {(lang === "id" ? eq.name_id : eq.name_en)} · {formatIDR(eq.price_idr)}
                </option>
              ))}
            </select>
            {selectedEq && (
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                {lang === "id"
                  ? `Add-on ${formatIDR(selectedEq.price_idr)} × ${pax} pax = ${formatIDR(eqTotal)}`
                  : `Add-on ${formatIDR(selectedEq.price_idr)} × ${pax} pax = ${formatIDR(eqTotal)}`}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="bk-notes" className="text-xs uppercase tracking-wider text-muted-foreground">{t("booking.notes")}</Label>
            <Textarea id="bk-notes" data-testid="booking-notes" rows={3} value={form.notes} onChange={handle("notes")} className="mt-1.5 bg-white" />
          </div>

          {/* Summary */}
          {selectedEq && (
            <div className="rounded-lg bg-muted/60 border border-border p-3" data-testid="booking-summary">
              <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
                {lang === "id" ? "Ringkasan Add-on" : "Add-on Summary"}
              </p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground/80">
                  {lang === "id" ? selectedEq.name_id : selectedEq.name_en}
                </span>
                <span className="font-serif text-primary font-semibold">
                  {formatIDR(eqTotal)}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {lang === "id"
                  ? "Total ini adalah add-on perlengkapan (belum termasuk harga paket umrah)."
                  : "This total is the equipment add-on only (does not include umrah package price)."}
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="booking-submit"
            className="btn-primary w-full justify-center disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {loading ? t("common.sending") : t("booking.submit")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
